// 📑 Relatório pronto "Gastos por filial" (05/11/2026, pedido do André para o fluxo Obras).
// Uma linha por card com TODOS os campos da etapa de entrada (Triagem), etapa atual, datas e valores;
// resumo por filial (rateio igual quando o card marca mais de uma filial) e exportação Excel/CSV.
// Genérico: aparece em qualquer fluxo que tenha um campo "Filial/Filiais" (ver fluxoTemRelatorioFilial).
import { useMemo, useState } from 'react';
import type { Card, Etapa, Flow } from '../../types';
import { textoParcelas } from '../CampoParcelas';
import { textoItens } from '../CampoItens';
import { formatShortCode } from '../../utils/shortCodeUtils';

const norm = (t: any) => String(t ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

const ordenarEtapas = (etapas: Etapa[]) =>
  [...etapas].sort((a, b) => (a.ordemFluxo ?? 999) - (b.ordemFluxo ?? 999));

const etapaBase = (flow: Flow): Etapa | undefined => {
  const etapas = flow.etapas || [];
  return etapas.find(e => norm(e.nome).includes('triagem')) || ordenarEtapas(etapas)[0];
};

const ehCampoFilial = (nome: string) => /^filia/.test(norm(nome)) || norm(nome).includes('filial');

const campoFilialDoFluxo = (flow: Flow) => {
  const base = etapaBase(flow);
  const naBase = (base?.campos || []).find(c => ehCampoFilial(c.nome));
  if (naBase) return naBase;
  for (const e of flow.etapas || []) {
    const c = (e.campos || []).find(cc => ehCampoFilial(cc.nome));
    if (c) return c;
  }
  return undefined;
};

export const fluxoTemRelatorioFilial = (flow: Flow) => !!campoFilialDoFluxo(flow);

// "250,00" / "1.234,56" / "1532.00" / "6.350" (milhar) / "R$ 99,90\n"
export const lerValorBR = (v: any): number => {
  if (v === undefined || v === null || v === '') return 0;
  if (typeof v === 'number') return isNaN(v) ? 0 : v;
  const s = String(v).replace(/R\$/gi, '').replace(/\s/g, '');
  if (!s) return 0;
  let n: number;
  if (s.includes(',')) n = Number(s.replace(/\./g, '').replace(',', '.'));
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) n = Number(s.replace(/\./g, ''));
  else n = Number(s);
  return isNaN(n) ? 0 : n;
};

const moeda = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const num2 = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Data no fuso de Brasília -> 'aaaa-mm-dd' (chave para filtrar) e 'dd/mm/aaaa' (tela)
const chaveDataBR = (v: any): string => {
  if (!v) return '';
  const s = String(v);
  // campo data/data_vencimento gravado sem fuso ("2026-11-05" ou "2026-11-05T08:03"): já é hora local
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
};
const dataTela = (chave: string) => chave ? chave.split('-').reverse().join('/') : '';

const valorDoCampo = (card: Card, campoId: string): any => {
  const v = card.campos?.[campoId];
  if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) return v;
  const hist = card.etapaHistory || [];
  for (let i = hist.length - 1; i >= 0; i--) {
    const s = hist[i]?.camposSnapshot?.[campoId];
    if (s !== undefined && s !== null && s !== '' && !(Array.isArray(s) && s.length === 0)) return s;
  }
  return undefined;
};

const textoDoCampo = (tipo: string, v: any): string => {
  if (v === undefined || v === null || v === '') return '';
  if (tipo === 'itens') return textoItens(v);
  if (tipo === 'parcelas') return textoParcelas(v);
  if (tipo === 'data' || tipo === 'data_vencimento') return dataTela(chaveDataBR(v));
  if (tipo === 'valor') return num2(lerValorBR(v));
  if (Array.isArray(v)) {
    return v.map(x => (x && typeof x === 'object') ? (x.name || x.nome || x.fileName || x.label || '') : String(x))
      .filter(Boolean).join(', ');
  }
  if (typeof v === 'object') return v.name || v.nome || v.label || v.valor || '';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  return String(v).replace(/\s+/g, ' ').trim();
};

type BaseValor = 'nota' | 'liquido' | 'triagem';
type FiltroEtapa = 'todas' | 'sem_reprovados' | 'concluidos' | 'andamento';
type BaseData = 'criacao' | 'conclusao' | 'vencimento';

interface Props {
  flow: Flow;
  cards: Card[];
  onVoltar: () => void;
}

const RelatorioGastosFilial = ({ flow, cards, onVoltar }: Props) => {
  const base = etapaBase(flow);
  const campoFilial = campoFilialDoFluxo(flow);
  const todosCampos = (flow.etapas || []).flatMap(e => e.campos || []);
  const campoValorTriagem = (base?.campos || []).find(c => c.tipo === 'valor');
  const campoBruto = todosCampos.find(c => c.tipo === 'valor' && norm(c.nome).includes('bruto'));
  const campoLiquido = todosCampos.find(c => c.tipo === 'valor' && norm(c.nome).includes('liquido'));
  const campoVenc = (base?.campos || []).find(c => norm(c.nome).includes('vencimento'))
    || todosCampos.find(c => norm(c.nome).includes('vencimento'));
  const etapaConcluida = (flow.etapas || []).find(e => /^conclu/.test(norm(e.nome)));
  const etapaReprovada = (flow.etapas || []).find(e => norm(e.nome).startsWith('reprova'));
  const nomeEtapa = useMemo(() => {
    const m = new Map<string, string>();
    (flow.etapas || []).forEach(e => m.set(String(e.id), e.nome));
    return m;
  }, [flow.etapas]);

  // Colunas da Triagem (todas, na ordem da etapa), sem a de filial, que vira coluna própria
  const colunasTriagem = (base?.campos || []).filter(c => c.id !== campoFilial?.id && c.tipo !== 'documento');

  const [baseValor, setBaseValor] = useState<BaseValor>(campoBruto ? 'nota' : 'triagem');
  const [filtroEtapa, setFiltroEtapa] = useState<FiltroEtapa>(etapaReprovada ? 'sem_reprovados' : 'todas');
  const [baseData, setBaseData] = useState<BaseData>('criacao');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [filial, setFilial] = useState('');
  const [busca, setBusca] = useState('');
  const [limite, setLimite] = useState(200);

  const linhas = useMemo(() => cards.map(card => {
    const filiaisBrutas = campoFilial ? valorDoCampo(card, String(campoFilial.id)) : undefined;
    const filiais: string[] = (Array.isArray(filiaisBrutas) ? filiaisBrutas : (filiaisBrutas ? [filiaisBrutas] : []))
      .map((f: any) => String(f).trim()).filter(Boolean);
    const vTriagem = campoValorTriagem ? lerValorBR(valorDoCampo(card, String(campoValorTriagem.id))) : 0;
    const vBruto = campoBruto ? lerValorBR(valorDoCampo(card, String(campoBruto.id))) : 0;
    const vLiquido = campoLiquido ? lerValorBR(valorDoCampo(card, String(campoLiquido.id))) : 0;
    const valor = baseValor === 'nota' ? (vBruto || vTriagem)
      : baseValor === 'liquido' ? (vLiquido || vTriagem) : vTriagem;
    const origemValor = baseValor === 'nota' && vBruto ? 'Nota (bruto)'
      : baseValor === 'liquido' && vLiquido ? 'Nota (líquido)' : 'Triagem';
    const criado = chaveDataBR((card as any).criadoEm || card.createdAt);
    let concluido = '';
    if (etapaConcluida && String(card.etapaId) === String(etapaConcluida.id)) {
      const h = (card.etapaHistory || []).filter(x => String(x.etapaId) === String(etapaConcluida.id));
      concluido = chaveDataBR(h.length ? h[h.length - 1].movedAt : card.updatedAt);
    }
    const venc = campoVenc ? chaveDataBR(valorDoCampo(card, String(campoVenc.id))) : '';
    const triagem: Record<string, string> = {};
    colunasTriagem.forEach(c => { triagem[String(c.id)] = textoDoCampo(c.tipo, valorDoCampo(card, String(c.id))); });
    return {
      card, codigo: formatShortCode(card.id), titulo: card.titulo || '', etapa: nomeEtapa.get(String(card.etapaId)) || '-',
      filiais, valor, origemValor, vTriagem, vBruto, vLiquido, criado, concluido, venc, triagem,
    };
  }), [cards, baseValor, campoFilial?.id, campoValorTriagem?.id, campoBruto?.id, campoLiquido?.id, nomeEtapa]);

  const filtradas = useMemo(() => {
    const b = norm(busca);
    return linhas.filter(l => {
      const etapaId = String(l.card.etapaId);
      if (filtroEtapa === 'sem_reprovados' && etapaReprovada && etapaId === String(etapaReprovada.id)) return false;
      if (filtroEtapa === 'concluidos' && (!etapaConcluida || etapaId !== String(etapaConcluida.id))) return false;
      if (filtroEtapa === 'andamento' && ((etapaConcluida && etapaId === String(etapaConcluida.id))
        || (etapaReprovada && etapaId === String(etapaReprovada.id)))) return false;
      const d = baseData === 'criacao' ? l.criado : baseData === 'conclusao' ? l.concluido : l.venc;
      if ((de || ate) && !d) return false;
      if (de && d < de) return false;
      if (ate && d > ate) return false;
      if (filial && !(filial === '(sem filial)' ? l.filiais.length === 0 : l.filiais.includes(filial))) return false;
      if (b && !norm([l.codigo, l.titulo, l.etapa, ...Object.values(l.triagem)].join(' ')).includes(b)) return false;
      return true;
    });
  }, [linhas, filtroEtapa, baseData, de, ate, filial, busca]);

  // Resumo por filial: card com N filiais entra com valor/N em cada uma (rateio igual).
  // Com filtro de filial, o resumo mostra só a parte daquela filial.
  const resumo = useMemo(() => {
    const m = new Map<string, { filial: string; cards: number; valor: number }>();
    filtradas.forEach(l => {
      const fs = l.filiais.length ? l.filiais : ['(sem filial)'];
      fs.forEach(f => {
        if (filial && f !== filial) return;
        const r = m.get(f) || { filial: f, cards: 0, valor: 0 };
        r.cards += 1; r.valor += l.valor / fs.length;
        m.set(f, r);
      });
    });
    return [...m.values()].sort((a, b) => b.valor - a.valor);
  }, [filtradas, filial]);
  const totalResumo = resumo.reduce((s, r) => s + r.valor, 0);

  const opcoesFilial = useMemo(() => {
    const s = new Set<string>();
    linhas.forEach(l => l.filiais.forEach(f => s.add(f)));
    const lista = [...s].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));
    if (linhas.some(l => l.filiais.length === 0)) lista.push('(sem filial)');
    return lista;
  }, [linhas]);

  // ---------- exportação ----------
  const cabecalho = [
    'Card', 'Título', 'Etapa atual', 'Criado em', 'Concluído em',
    campoFilial?.nome || 'Filial', 'Nº de filiais',
    ...colunasTriagem.map(c => c.nome),
    ...(campoBruto ? [campoBruto.nome] : []), ...(campoLiquido ? [campoLiquido.nome] : []),
    'Valor considerado', 'Origem do valor', 'Valor por filial (rateio)',
  ];
  const colunasNumericas = new Set<number>();
  const valoresLinha = (l: typeof linhas[0]): (string | number)[] => {
    const vals: (string | number)[] = [
      l.codigo, l.titulo, l.etapa, dataTela(l.criado), dataTela(l.concluido),
      l.filiais.join(', ') || '(sem filial)', l.filiais.length,
      ...colunasTriagem.map(c => c.tipo === 'valor' ? lerValorBR(valorDoCampo(l.card, String(c.id))) : l.triagem[String(c.id)]),
      ...(campoBruto ? [l.vBruto] : []), ...(campoLiquido ? [l.vLiquido] : []),
      l.valor, l.origemValor, l.valor / Math.max(1, l.filiais.length),
    ];
    return vals;
  };
  // índices de colunas em R$ (para formatar no Excel)
  colunasTriagem.forEach((c, i) => { if (c.tipo === 'valor') colunasNumericas.add(7 + i); });
  { let i = 7 + colunasTriagem.length; if (campoBruto) colunasNumericas.add(i++); if (campoLiquido) colunasNumericas.add(i++);
    colunasNumericas.add(i); colunasNumericas.add(i + 2); }

  const nomeArquivo = () => {
    const hoje = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
    return `gastos_por_filial_${norm(flow.name).replace(/ /g, '_').slice(0, 30)}_${hoje}`;
  };
  const baixar = (conteudo: string, tipo: string, ext: string) => {
    const blob = new Blob(['\ufeff' + conteudo], { type: tipo });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${nomeArquivo()}.${ext}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };
  const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const descricaoFiltros = () => [
    `Fluxo: ${flow.name}`,
    `Etapas: ${({ todas: 'todas', sem_reprovados: 'todas menos Reprovados', concluidos: 'só Concluídos', andamento: 'só em andamento' } as any)[filtroEtapa]}`,
    `Período (${({ criacao: 'criação', conclusao: 'conclusão', vencimento: 'vencimento' } as any)[baseData]}): ${de ? dataTela(de) : 'início'} a ${ate ? dataTela(ate) : 'hoje'}`,
    `Filial: ${filial || 'todas'}`,
    `Valor: ${({ nota: 'bruto da nota; sem nota, o da Triagem', liquido: 'líquido a pagar; sem nota, o da Triagem', triagem: 'o da Triagem' } as any)[baseValor]}`,
    'Card com várias filiais: valor dividido igualmente entre elas',
  ].join(' | ');

  const exportarExcel = () => {
    const tdNum = (n: number) => `<td x:num="${n.toFixed(2)}" style="mso-number-format:'\\#\\,\\#\\#0\\.00'">${num2(n)}</td>`;
    let html = '<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body>';
    html += `<table><tr><td colspan="4"><b>Gastos por filial</b></td></tr><tr><td colspan="8">${esc(descricaoFiltros())}</td></tr></table><br>`;
    html += '<table border="1"><thead><tr><th>Filial</th><th>Cards</th><th>Total (R$)</th><th>% do total</th></tr></thead><tbody>';
    resumo.forEach(r => {
      html += `<tr><td>${esc(r.filial)}</td><td x:num="${r.cards}">${r.cards}</td>${tdNum(r.valor)}<td>${totalResumo ? (r.valor / totalResumo * 100).toFixed(1).replace('.', ',') : '0'}%</td></tr>`;
    });
    html += `<tr><td><b>Total</b></td><td x:num="${filtradas.length}"><b>${filtradas.length}</b></td>${tdNum(totalResumo)}<td>100%</td></tr></tbody></table><br>`;
    html += '<table border="1"><thead><tr>' + cabecalho.map(h => `<th>${esc(h)}</th>`).join('') + '</tr></thead><tbody>';
    filtradas.forEach(l => {
      html += '<tr>' + valoresLinha(l).map((v, i) => colunasNumericas.has(i) ? tdNum(Number(v) || 0)
        : (typeof v === 'number' ? `<td x:num="${v}">${v}</td>` : `<td style="mso-number-format:'\\@'">${esc(v)}</td>`)).join('') + '</tr>';
    });
    html += '</tbody></table></body></html>';
    baixar(html, 'application/vnd.ms-excel;charset=utf-8', 'xls');
  };

  const exportarCSV = () => {
    const cel = (v: any, i: number) => {
      const s = colunasNumericas.has(i) ? num2(Number(v) || 0).replace(/\./g, '') : String(v ?? '');
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [cabecalho.map(h => `"${h.replace(/"/g, '""')}"`).join(';'),
      ...filtradas.map(l => valoresLinha(l).map(cel).join(';'))].join('\r\n');
    baixar(csv, 'text/csv;charset=utf-8', 'csv');
  };

  const selectCls = 'px-3 py-2 rounded-lg text-sm border border-gray-300 bg-white text-gray-700';

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-4 md:p-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Gastos por filial</h2>
              <p className="text-sm text-gray-600">
                Um card por linha, com todos os campos da {base?.nome || 'primeira etapa'}. Card com várias filiais divide o valor igualmente.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={onVoltar} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors">
                ← Tabela dinâmica
              </button>
              <button onClick={exportarExcel} disabled={filtradas.length === 0}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors">
                Excel
              </button>
              <button onClick={exportarCSV} disabled={filtradas.length === 0}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg transition-colors">
                CSV
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="text-xs text-gray-600 flex flex-col gap-1">Período por
              <select value={baseData} onChange={e => setBaseData(e.target.value as BaseData)} className={selectCls}>
                <option value="criacao">Data de criação</option>
                {etapaConcluida && <option value="conclusao">Data de conclusão</option>}
                {campoVenc && <option value="vencimento">{campoVenc.nome}</option>}
              </select>
            </label>
            <label className="text-xs text-gray-600 flex flex-col gap-1">De
              <input type="date" value={de} onChange={e => setDe(e.target.value)} className={selectCls} />
            </label>
            <label className="text-xs text-gray-600 flex flex-col gap-1">Até
              <input type="date" value={ate} onChange={e => setAte(e.target.value)} className={selectCls} />
            </label>
            <label className="text-xs text-gray-600 flex flex-col gap-1">Filial
              <select value={filial} onChange={e => setFilial(e.target.value)} className={selectCls}>
                <option value="">Todas</option>
                {opcoesFilial.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600 flex flex-col gap-1">Etapas
              <select value={filtroEtapa} onChange={e => setFiltroEtapa(e.target.value as FiltroEtapa)} className={selectCls}>
                <option value="todas">Todas</option>
                {etapaReprovada && <option value="sem_reprovados">Todas menos {etapaReprovada.nome}</option>}
                {etapaConcluida && <option value="concluidos">Só {etapaConcluida.nome}</option>}
                <option value="andamento">Só em andamento</option>
              </select>
            </label>
            <label className="text-xs text-gray-600 flex flex-col gap-1">Valor
              <select value={baseValor} onChange={e => setBaseValor(e.target.value as BaseValor)} className={selectCls}>
                {campoBruto && <option value="nota">{campoBruto.nome} (sem nota: {campoValorTriagem?.nome || 'Triagem'})</option>}
                {campoLiquido && <option value="liquido">{campoLiquido.nome} (sem nota: {campoValorTriagem?.nome || 'Triagem'})</option>}
                <option value="triagem">{campoValorTriagem?.nome || 'Valor da Triagem'}</option>
              </select>
            </label>
            <label className="text-xs text-gray-600 flex flex-col gap-1">Buscar
              <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="título, fornecedor, nome..." className={selectCls} />
            </label>
            {(de || ate || filial || busca) && (
              <button onClick={() => { setDe(''); setAte(''); setFilial(''); setBusca(''); }}
                className="px-3 py-2 text-sm text-blue-700 hover:underline">Limpar filtros</button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Total por filial</h3>
            <span className="text-sm text-gray-600">{filtradas.length} cards · <b className="text-gray-900">{moeda(totalResumo)}</b></span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="text-left text-gray-600 border-b">
                <th className="py-2 pr-4">Filial</th><th className="py-2 pr-4 text-right">Cards</th>
                <th className="py-2 pr-4 text-right">Total</th><th className="py-2 pr-4 w-1/3">% do total</th>
              </tr></thead>
              <tbody>
                {resumo.map(r => {
                  const pct = totalResumo ? r.valor / totalResumo * 100 : 0;
                  return (
                    <tr key={r.filial} className="border-b last:border-0 hover:bg-blue-50 cursor-pointer"
                      title="Clique para ver só os cards desta filial"
                      onClick={() => setFilial(filial === r.filial ? '' : r.filial)}>
                      <td className="py-1.5 pr-4 font-medium text-gray-900">{r.filial}</td>
                      <td className="py-1.5 pr-4 text-right">{r.cards}</td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">{moeda(r.valor)}</td>
                      <td className="py-1.5 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="h-2 bg-gray-100 rounded flex-1"><div className="h-2 bg-blue-600 rounded" style={{ width: `${pct}%` }} /></div>
                          <span className="text-xs text-gray-600 w-12 text-right">{pct.toFixed(1).replace('.', ',')}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {resumo.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-500">Nenhum card com esses filtros</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Cards</h3>
            <span className="text-xs text-gray-500">
              {filtradas.length > limite ? `Mostrando ${limite} de ${filtradas.length}; a exportação traz todos` : `${filtradas.length} cards`}
            </span>
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto border rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0"><tr className="text-left text-gray-600">
                {cabecalho.map(h => <th key={h} className="px-2 py-2 whitespace-nowrap border-b">{h}</th>)}
              </tr></thead>
              <tbody>
                {filtradas.slice(0, limite).map(l => (
                  <tr key={l.card.id} className="border-b hover:bg-gray-50 align-top">
                    {valoresLinha(l).map((v, i) => (
                      <td key={i} className={`px-2 py-1 ${colunasNumericas.has(i) ? 'text-right tabular-nums whitespace-nowrap' : ''} ${i === 1 ? 'min-w-[200px]' : ''}`}>
                        <div className="max-w-[320px] truncate" title={String(v ?? '')}>
                          {colunasNumericas.has(i) ? (Number(v) ? num2(Number(v)) : '') : String(v ?? '')}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtradas.length > limite && (
            <button onClick={() => setLimite(limite + 500)} className="mt-3 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">
              Mostrar mais
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelatorioGastosFilial;
